import { resolveCorrectOrder } from "@memorang/helpers/ordering";
import type {
	BaseItemSchema,
	Choice,
	ItemResponseFormField,
} from "@memorang/types";
import {
	countCorrectDictationWords,
	getDictationAnswerKeyWords,
	isWriteFromDictation,
} from "../../helpers/dictation";
import {
	isBlankDropdown,
	isMatchingPair,
} from "../../helpers/item-type-guards";
import { isTextAnswerMatch } from "../../helpers/text-match";
import type { ItemState } from "../../hooks/types";

const isSataAnswerCorrect = (
	choices: Choice[],
	selectedChoiceIds?: string[],
) => {
	const numRequiredAnswers = choices.filter(
		({ isCorrect }) => isCorrect,
	).length;
	const numCorrectSelectedChoiceIds = selectedChoiceIds
		? selectedChoiceIds.filter((selectedChoiceId) => {
				const selectedChoice = choices.find(
					(item) => item.id === selectedChoiceId,
				);
				return selectedChoice ? selectedChoice.isCorrect : false;
			}).length
		: 0;
	const numCorrectChoiceIds = choices.filter(
		({ isCorrect }) => isCorrect,
	).length;

	const isCorrect = numRequiredAnswers
		? numCorrectSelectedChoiceIds >= numRequiredAnswers &&
			selectedChoiceIds?.length === numCorrectSelectedChoiceIds
		: numCorrectChoiceIds === numCorrectSelectedChoiceIds;

	return isCorrect;
};

const normalizeFormAnswer = (value?: string) =>
	value?.trim().toLocaleLowerCase();

export const getAnsweredFormStates = (
	fields: ItemResponseFormField[],
	currentFormStates?: Record<string, string>,
) => {
	return Object.fromEntries(
		Object.entries(currentFormStates || {}).map(([fieldId, fieldState]) => {
			const field = fields.find((f) => f.id === fieldId);
			const userAnswer = normalizeFormAnswer(fieldState);
			const isCorrect = field?.expectedResponse
				? field.expectedResponse.some(
						(r) => normalizeFormAnswer(r) === userAnswer,
					)
				: userAnswer === normalizeFormAnswer(field?.expected_response);
			return [fieldId, { response: fieldState, isCorrect }];
		}),
	);
};

const isNoteCompletionAnswerCorrect = (
	fields: ItemResponseFormField[],
	currentFormStates?: Record<string, string>,
) => {
	const isCorrect = fields.every((field) => {
		const fieldState = currentFormStates?.[field.id] || "";
		const userAnswer = normalizeFormAnswer(fieldState);
		if (field.expectedResponse) {
			return field.expectedResponse.some(
				(r) => normalizeFormAnswer(r) === userAnswer,
			);
		}
		return userAnswer === normalizeFormAnswer(field.expected_response);
	});
	return isCorrect;
};

export type GetIsCorrectAnswerParams = {
	currentItem: BaseItemSchema;
	currentItemState?: ItemState;
	currentFormStates?: Record<string, string>;
	choices: Choice[];
	selectedChoiceIds: string[];
	selectedChoiceItemId?: string;
	answerId?: string;
	resolvedCorrectAnswerIds: string[];
	selectedBlankValues?: Record<string, string>;
	selectedOrder?: string[];
	userResponseText?: string;
};

export const getIsCorrectAnswer = ({
	currentItem,
	currentItemState,
	currentFormStates,
	choices,
	selectedChoiceIds,
	selectedChoiceItemId,
	answerId,
	resolvedCorrectAnswerIds,
	selectedBlankValues,
	selectedOrder,
	userResponseText,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: one branch per response/item-type scoring shape
}: GetIsCorrectAnswerParams): boolean | undefined => {
	const { response } = currentItem;
	const itemType = currentItem.metadata?.type;
	const isSequence = response?.type === "reorder";
	const correctAnswerOrders =
		response.type === "reorder"
			? response.correctResponse?.map((choice: { id: string }) => choice.id)
			: undefined;
	const isNoteCompletion = response?.type === "form";

	if (
		(response.type === "TOKEN_MAPPING" ||
			response.type === "INLINE_OPTION_SELECTION") &&
		currentItem.scoring &&
		"type" in currentItem.scoring &&
		currentItem.scoring.type === "ANSWER_KEY_MAPPING"
	) {
		// PTE FIBDDR/FIBDPR store their blank map in useAIFeedbackStore
		// (selectedBlankValues); TOEIC's BLANK_DRAG_WORD shares the same
		// response.type but stores it in session formStates instead.
		return currentItem.scoring.answerKey.every((entry) => {
			if (!("blankId" in entry)) {
				return false;
			}
			const blankValue =
				selectedBlankValues?.[entry.blankId] ??
				currentFormStates?.[entry.blankId];
			return blankValue === ("wordId" in entry ? entry.wordId : entry.choiceId);
		});
	}
	if (
		response.type === "ORDER_RECONSTRUCTION" &&
		currentItem.scoring &&
		"type" in currentItem.scoring &&
		currentItem.scoring.type === "ANSWER_KEY_ORDERING"
	) {
		const correctOrderIds = resolveCorrectOrder(
			currentItem.scoring.answerKey,
			currentItem.scoring.correctOrder,
		);
		return (
			correctOrderIds != null &&
			selectedOrder != null &&
			selectedOrder.length === correctOrderIds.length &&
			selectedOrder.every((id, index) => id === correctOrderIds[index])
		);
	}
	if (
		isMatchingPair(currentItem) &&
		currentItem.scoring &&
		"type" in currentItem.scoring &&
		currentItem.scoring.type === "ANSWER_KEY_MAPPING"
	) {
		return currentItem.scoring.answerKey.every(
			(entry) =>
				"leftId" in entry &&
				currentFormStates?.[entry.leftId] === entry.rightId,
		);
	}
	if (isBlankDropdown(currentItem)) {
		if (!("blanks" in response)) {
			return false;
		}
		return response.blanks.every((blank) => {
			const selectedId = currentFormStates?.[blank.blankId];
			return blank.choices.find((c) => c.id === selectedId)?.isCorrect === true;
		});
	}
	if (
		(itemType === "FIBTIL" || itemType === "FIBTIL_CORE") &&
		response.type === "SHORT_TEXT_ENTRY" &&
		currentItem.scoring &&
		"type" in currentItem.scoring &&
		currentItem.scoring.type === "ANSWER_KEY_TEXT"
	) {
		return currentItem.scoring.answerKey.every(
			(entry) =>
				"blankId" in entry &&
				isTextAnswerMatch(selectedBlankValues?.[entry.blankId], entry.word),
		);
	}
	if (isWriteFromDictation(itemType)) {
		const answerKeyWords = getDictationAnswerKeyWords(currentItem);
		if (!answerKeyWords) {
			return false;
		}
		return (
			countCorrectDictationWords(answerKeyWords, userResponseText ?? "") ===
			answerKeyWords.length
		);
	}
	if (isSequence) {
		return correctAnswerOrders?.every((order: string) =>
			currentItemState?.currentOrders?.includes(order),
		);
	}
	if (response?.type === "choice" && response.selectionType === "multiple") {
		return isSataAnswerCorrect(choices, selectedChoiceIds);
	}
	if (isNoteCompletion) {
		return isNoteCompletionAnswerCorrect(
			response.fields || [],
			currentFormStates,
		);
	}
	if (response.type === "INLINE_TEXT_SELECTION") {
		const correctSet = new Set(resolvedCorrectAnswerIds);
		return (
			selectedChoiceIds.length === correctSet.size &&
			selectedChoiceIds.every((id) => correctSet.has(id))
		);
	}
	return selectedChoiceItemId != null && selectedChoiceItemId === answerId;
};
